import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, DownloadSimple, Sparkle, Trash, X } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const LETTER_KINDS = [
  {k:"offer", label:"Offer Letter"},
  {k:"appointment", label:"Appointment Letter"},
  {k:"confirmation", label:"Confirmation Letter"},
  {k:"appraisal", label:"Appraisal Letter"},
  {k:"termination", label:"Termination Letter"},
  {k:"relieving", label:"Relieving Letter"},
];

const emptyDraft = { employee_name:"", employee_email:"", kind:"offer", designation:"", department:"", salary:0, joining_date:"", last_working_date:"", notes:"" };

const todayISO = () => new Date().toISOString().slice(0, 10);
const formatIssueDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

export default function Letters() {
  const [letters, setLetters] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("compose"); // compose | preview
  const [draft, setDraft] = useState(emptyDraft);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [issueDate, setIssueDate] = useState(todayISO);  // stored as YYYY-MM-DD, formatted at save
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftIssueDate, setDraftIssueDate] = useState(""); // the issue_date the current body was drafted with

  const load = async () => {
    const { data } = await api.get("/hr/letters");
    setLetters(data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setDraft(emptyDraft); setBody(""); setSubject(""); setIssueDate(todayISO()); setDraftIssueDate(""); setStep("compose"); };

  const generate = async () => {
    if (!draft.employee_name.trim()) { toast.error("Employee name required"); return; }
    setGenerating(true);
    try {
      const formattedIssue = formatIssueDate(issueDate);
      const { data } = await api.post("/hr/letters/draft", { ...draft, salary: Number(draft.salary)||0, issue_date: formattedIssue });
      setBody(data.body); setSubject(data.subject); setDraftIssueDate(issueDate); setStep("preview");
      toast.success("AI draft ready · edit before saving");
    } catch (e) {
      toast.error("Draft failed");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/hr/letters", {
        employee_name: draft.employee_name,
        employee_email: draft.employee_email,
        kind: draft.kind,
        subject, body, issue_date: formatIssueDate(issueDate),
        metadata: { designation: draft.designation, department: draft.department, salary: Number(draft.salary)||0, joining_date: draft.joining_date, last_working_date: draft.last_working_date, notes: draft.notes },
      });
      toast.success("Letter saved");
      setOpen(false); reset(); load();
      // auto-open PDF
      setTimeout(()=>window.open(`${API}/hr/letters/${data.id}/pdf`, "_blank"), 400);
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this letter?")) return;
    await api.delete(`/hr/letters/${id}`);
    setLetters((curr) => curr.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <Layout title="HR Letters" subtitle="AI-drafted · Shelves Tech letterhead · HR signature"
      actions={<button onClick={()=>{ reset(); setOpen(true); }} data-testid="new-letter-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]"><FileText size={16} weight="bold"/> New Letter</button>}>
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="letters-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>{["Employee","Type","Subject","Issued",""].map(h => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {letters.length===0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-[#717680]">No letters yet — click &ldquo;New Letter&rdquo; to draft one with AI.</td></tr>}
            {letters.map(l => (
              <tr key={l.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`ltr-row-${l.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{l.employee_name}</div><div className="text-[10px] text-[#717680]">{l.employee_email}</div></td>
                <td className="px-4 py-3"><span className="text-[10px] font-mono-shelv tracking-widest text-[#D90429] border border-[#D90429]/30 px-2 py-1 bg-[#FFE5E9]/40 uppercase">{l.kind}</span></td>
                <td className="px-4 py-3 text-[#4A4D57] truncate max-w-[300px]">{l.subject}</td>
                <td className="px-4 py-3 text-[#4A4D57] font-mono-shelv text-xs">{l.issue_date}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`${API}/hr/letters/${l.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-[#0A0A0A] text-white text-[11px] font-bold hover:bg-[#D90429] mr-1" data-testid={`download-${l.id}`}><DownloadSimple size={12} weight="bold"/> PDF</a>
                  <button onClick={()=>remove(l.id)} className="p-1.5 text-[#D90429] hover:bg-[#D90429]/10" data-testid={`del-ltr-${l.id}`}><Trash size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{step==="compose"?"Draft new letter":"Preview & edit"}</DialogTitle></DialogHeader>
          {step==="compose" ? (
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Letter Type *</Label>
                <Select value={draft.kind} onValueChange={v=>setDraft({...draft,kind:v})}>
                  <SelectTrigger className="rounded-none" data-testid="kind-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{LETTER_KINDS.map(l=><SelectItem key={l.k} value={l.k}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Employee Name *</Label><Input value={draft.employee_name} onChange={e=>setDraft({...draft,employee_name:e.target.value})} className="rounded-none" data-testid="emp-name-input"/></div>
              <div><Label>Employee Email</Label><Input value={draft.employee_email} onChange={e=>setDraft({...draft,employee_email:e.target.value})} className="rounded-none"/></div>
              <div><Label>Designation</Label><Input value={draft.designation} onChange={e=>setDraft({...draft,designation:e.target.value})} className="rounded-none"/></div>
              <div><Label>Department</Label><Input value={draft.department} onChange={e=>setDraft({...draft,department:e.target.value})} className="rounded-none"/></div>
              <div><Label>Annual CTC (₹)</Label><Input type="number" value={draft.salary} onChange={e=>setDraft({...draft,salary:e.target.value})} className="rounded-none"/></div>
              <div><Label>Joining / Effective Date</Label><Input type="date" value={draft.joining_date} onChange={e=>setDraft({...draft,joining_date:e.target.value})} className="rounded-none"/></div>
              <div><Label>Last Working Day (if relevant)</Label><Input type="date" value={draft.last_working_date} onChange={e=>setDraft({...draft,last_working_date:e.target.value})} className="rounded-none"/></div>
              <div>
                <Label>Letter Issue Date *</Label>
                <Input type="date" value={issueDate} onChange={e=>setIssueDate(e.target.value)} className="rounded-none" data-testid="issue-date-compose"/>
                <div className="text-[10px] text-[#717680] mt-1">Defaults to today · edit if backdating or postdating. Prints as <b>{formatIssueDate(issueDate) || "—"}</b> on the PDF.</div>
              </div>
              <div className="col-span-2"><Label>Additional notes for AI</Label><Textarea value={draft.notes} onChange={e=>setDraft({...draft,notes:e.target.value})} rows={2} placeholder="e.g., probation 6 months, hybrid model, etc." className="rounded-none"/></div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label>Subject</Label><Input value={subject} onChange={e=>setSubject(e.target.value)} className="rounded-none" data-testid="subject-input"/></div>
              <div>
                <Label>Issue Date (prints as {formatIssueDate(issueDate) || "—"})</Label>
                <Input type="date" value={issueDate} onChange={e=>setIssueDate(e.target.value)} className="rounded-none" data-testid="issue-date-preview"/>
              </div>
              {draftIssueDate && issueDate !== draftIssueDate && (
                <div className="border border-[#D90429] bg-[#FFE5E9]/60 px-3 py-2 text-[11px] text-[#0A0A0A] flex items-center justify-between gap-3" data-testid="date-drift-warning">
                  <span><b>Issue date changed</b> — body still references <b>{formatIssueDate(draftIssueDate)}</b>. Regenerate the body so AI-generated dates match the new issue date.</span>
                  <button onClick={generate} disabled={generating} data-testid="redraft-with-date-button" className="shrink-0 px-3 py-1.5 bg-[#D90429] text-white text-[11px] font-bold hover:bg-[#EF233C] disabled:opacity-50">
                    {generating?"Redrafting…":"Re-draft with new date"}
                  </button>
                </div>
              )}
              <div><Label>Body (HTML — &lt;p&gt; tags allowed)</Label>
                <Textarea value={body} onChange={e=>setBody(e.target.value)} rows={14} className="rounded-none font-mono text-xs" data-testid="body-input"/>
              </div>
              <div className="text-[10px] text-[#717680] font-mono-shelv bg-[#FFE5E9]/30 border border-[#D90429]/20 px-3 py-2">
                ★ The PDF will use the Shelves Tech letterhead and automatically append the HR signature block — you don&rsquo;t need to add it here.
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            {step==="compose" ? (
              <button onClick={generate} disabled={generating} data-testid="ai-draft-button" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#D90429] disabled:opacity-50">
                <Sparkle size={14} weight="bold"/> {generating?"AI drafting…":"AI Draft"}
              </button>
            ) : (
              <>
                <button onClick={()=>setStep("compose")} className="px-4 py-2 border border-black/15 text-sm">← Back</button>
                <button onClick={save} disabled={saving} data-testid="save-letter-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">
                  {saving?"Saving…":"Save & Download PDF"}
                </button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
